import { useEffect, useRef } from 'react';
import { isMusic24Locale } from 'fym-shared/src/music24/music24Locales';
import { useTRPCClientAPI } from '../../../trpc/trpc';
import { useUser } from '../../users/hooks/useUser';
import { useAppLocale } from './AppIntlProvider';
import { readStoredAppLocale, readStoredAppLocaleSource } from './appLocaleStorage';

/**
 * Saves a language that the person picked before the sign-in (music24.com or the sign-in page) to
 * the account, when the account has none yet ("Automatic"). The welcome page calls it, so a new
 * account keeps the language of the signup: the app, the emails and the push messages use it
 * (QA 2026-10-28, lit-1). A failed save keeps the language in this browser only.
 */
export function useSaveVisitorLocale(): void {
  const trpcClient = useTRPCClientAPI();
  const { data, refetch } = useUser();
  const { setPreference } = useAppLocale();
  const serverLocale = data?.user.music24Locale;
  const isDone = useRef(false);

  useEffect(() => {
    // `undefined`: no answer yet, or an older server with no field.
    if (isDone.current || serverLocale === undefined || isMusic24Locale(serverLocale)) {
      return;
    }
    const pick = readStoredAppLocaleSource() === 'visitor' ? readStoredAppLocale() : null;
    if (pick === null) {
      return;
    }
    isDone.current = true;
    trpcClient.users.updateLanguage
      .mutate({ locale: pick })
      .then(() => {
        setPreference(pick, 'account');
        refetch().catch(console.error);
      })
      .catch((error: unknown) => {
        console.error('Could not save the language of the signup to the account', error);
      });
  }, [refetch, serverLocale, setPreference, trpcClient]);
}
